import React, { useState, useEffect } from 'react';
import { analyticsService, TweetData } from '../services/analyticsService';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n';
import { useToast } from './Toast';

export const Analytics: React.FC = () => {
  const { t, locale } = useT();
  const { toast } = useToast();
  const days = [0, 1, 2, 3, 4, 5, 6].map(i => t(`analytics.days.${i}`));
  const [tweets, setTweets] = useState<TweetData[]>([]);
  const [selectedView, setSelectedView] = useState<'overview' | 'topics' | 'timing' | 'trends'>('overview');
  const [showImportMenu, setShowImportMenu] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setTweets(analyticsService.getTweets());
  };

  const metrics = analyticsService.calculateMetrics();
  const clusters = analyticsService.classifyTopics();
  const timeSlots = analyticsService.analyzeTimeSlots();
  const trendData = analyticsService.getTrendData(30);
  const insights = analyticsService.generateInsights();

  // CSVインポート
  const handleImportCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      const result = analyticsService.importFromCSV(content);
      
      if (result.success) {
        toast(t('analytics.importSuccess', { count: result.imported }), 'success');
        loadData();
      } else {
        toast(t('analytics.importError', { error: result.error || '' }), 'error');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // サンプルデータ生成
  const handleGenerateSample = () => {
    if (window.confirm(t('analytics.confirmSample'))) {
      analyticsService.generateSampleData(50);
      loadData();
    }
  };

  // ヒートマップ用データ変換（未使用だが将来用）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const heatmapData = () => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const data: { day: string; hour: number; value: number }[] = [];

    dayNames.forEach((day: string, dayIdx: number) => {
      hours.forEach(hour => {
        const slot = timeSlots.find(s => s.dayOfWeek === dayIdx && s.hour === hour);
        data.push({
          day,
          hour,
          value: slot ? slot.avgEngagement : 0,
        });
      });
    });

    return data;
  };

  if (tweets.length === 0) {
    // データなし
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="text-center py-16">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-text-primary mb-4">
            Analytics
          </h1>
          <p className="text-text-muted mb-8">
            {t('analytics.empty.subtitle')}
          </p>

          <div className="space-y-4">
            <div className="bg-surface-2 border border-border rounded-xl p-6">
              <p className="text-text-muted mb-4">{t('analytics.empty.message')}</p>
              
              <div className="flex flex-col gap-3 max-w-md mx-auto">
                <label className="px-4 py-3 bg-brand text-white rounded-lg hover:bg-brand/90 cursor-pointer text-center">
                  {t('analytics.empty.importCsv')}
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleImportCSV}
                    className="hidden"
                  />
                </label>

                <button
                  onClick={handleGenerateSample}
                  className="px-4 py-3 bg-surface-2 border border-border text-text-primary rounded-lg hover:bg-surface-1"
                >
                  {t('analytics.empty.generateSample')}
                </button>
              </div>

              <div className="mt-6 text-xs text-text-muted text-left max-w-md mx-auto">
                <p className="font-medium mb-2">{t('analytics.csvFormat')}</p>
                <pre className="bg-surface-1 p-3 rounded overflow-x-auto">
{`text,timestamp,likes,retweets,replies
"Had a great day!",2026-02-10T12:00:00Z,10,2,1
"New feature release",2026-02-09T18:00:00Z,25,5,3`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-1.5 tracking-tight">
              Analytics
            </h1>
            <p className="text-text-muted text-sm">
              {t('analytics.subtitle')}
            </p>
          </div>

          {/* Import Menu */}
          <div className="relative">
            <button
              onClick={() => setShowImportMenu(!showImportMenu)}
              className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-all"
            >
              {t('analytics.manage')}
            </button>
            
            {showImportMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-surface-2 border border-border rounded-lg shadow-lg z-10">
                <div className="p-2">
                  <label className="w-full block px-3 py-2 text-sm text-text-primary hover:bg-brand/10 rounded cursor-pointer">
                    {t('analytics.menu.import')}
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleImportCSV}
                      className="hidden"
                    />
                  </label>
                  <button
                    onClick={() => {
                      handleGenerateSample();
                      setShowImportMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-brand/10 rounded"
                  >
                    {t('analytics.menu.sample')}
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(t('analytics.confirmDelete'))) {
                        analyticsService.saveTweets([]);
                        loadData();
                      }
                      setShowImportMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-danger hover:bg-danger/10 rounded"
                  >
                    {t('analytics.menu.deleteAll')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* View Tabs */}
        <div className="flex gap-2 overflow-x-auto">
          {[
            { id: 'overview', label: t('analytics.tabs.overview') },
            { id: 'topics', label: t('analytics.tabs.topics') },
            { id: 'timing', label: t('analytics.tabs.timing') },
            { id: 'trends', label: t('analytics.tabs.trends') },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedView(tab.id as any)}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                selectedView === tab.id
                  ? 'bg-brand/10 text-brand font-semibold'
                  : 'bg-surface-2 border border-border text-text-primary hover:bg-surface-3'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Insights Banner */}
      <div className="mb-6 p-4 bg-brand/10 border border-brand/30 rounded-xl">
        <h3 className="text-lg font-bold text-brand mb-3">{t('analytics.insights')}</h3>
        <ul className="space-y-2">
          {insights.map((insight, i) => (
            <li key={i} className="text-sm text-text-primary">
              {insight}
            </li>
          ))}
        </ul>
      </div>

      {/* Overview */}
      {selectedView === 'overview' && (
        <div className="space-y-6">
          {/* Metrics Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-surface-2 border border-border rounded-xl p-4">
              <p className="text-text-muted text-xs mb-1">{t('analytics.metric.totalTweets')}</p>
              <p className="text-2xl font-bold text-text-primary">{metrics.totalTweets}</p>
            </div>
            <div className="bg-surface-2 border border-border rounded-xl p-4">
              <p className="text-text-muted text-xs mb-1">{t('analytics.metric.avgLikes')}</p>
              <p className="text-2xl font-bold text-warning">{metrics.avgLikes.toFixed(1)}</p>
            </div>
            <div className="bg-surface-2 border border-border rounded-xl p-4">
              <p className="text-text-muted text-xs mb-1">{t('analytics.metric.avgRt')}</p>
              <p className="text-2xl font-bold text-success">{metrics.avgRetweets.toFixed(1)}</p>
            </div>
            <div className="bg-surface-2 border border-border rounded-xl p-4">
              <p className="text-text-muted text-xs mb-1">{t('analytics.metric.avgReplies')}</p>
              <p className="text-2xl font-bold text-brand">{metrics.avgReplies.toFixed(1)}</p>
            </div>
          </div>

          {/* Best/Worst Tweets */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {metrics.bestTweet && (
              <div className="bg-surface-2 border border-success rounded-xl p-4">
                <h3 className="text-sm font-bold text-success mb-2">{t('analytics.best')}</h3>
                <p className="text-sm text-text-primary mb-2">{metrics.bestTweet.text}</p>
                <div className="flex gap-4 text-xs text-text-muted">
                  <span>{t('analytics.labels.likes')} {metrics.bestTweet.likes || 0}</span>
                  <span>{t('analytics.labels.rt')} {metrics.bestTweet.retweets || 0}</span>
                  <span>{t('analytics.labels.replies')} {metrics.bestTweet.replies || 0}</span>
                </div>
              </div>
            )}
            {metrics.worstTweet && (
              <div className="bg-surface-2 border border-border rounded-xl p-4">
                <h3 className="text-sm font-bold text-text-muted mb-2">{t('analytics.worst')}</h3>
                <p className="text-sm text-text-primary mb-2">{metrics.worstTweet.text}</p>
                <div className="flex gap-4 text-xs text-text-muted">
                  <span>{t('analytics.labels.likes')} {metrics.worstTweet.likes || 0}</span>
                  <span>{t('analytics.labels.rt')} {metrics.worstTweet.retweets || 0}</span>
                  <span>{t('analytics.labels.replies')} {metrics.worstTweet.replies || 0}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Topics */}
      {selectedView === 'topics' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <div className="bg-surface-2 border border-border rounded-xl p-6">
              <h3 className="text-lg font-bold text-text-primary mb-4">{t('analytics.topics.distribution')}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={clusters.map(c => ({ name: c.name, value: c.tweets.length }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {clusters.map((cluster, index) => (
                      <Cell key={`cell-${index}`} fill={cluster.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Engagement by Topic */}
            <div className="bg-surface-2 border border-border rounded-xl p-6">
              <h3 className="text-lg font-bold text-text-primary mb-4">{t('analytics.topics.engagement')}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={clusters}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2E2E35" />
                  <XAxis dataKey="name" stroke="#7B7B88" />
                  <YAxis stroke="#7B7B88" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#18181B', border: '1px solid #2E2E35' }}
                    labelStyle={{ color: '#F0F0F2' }}
                  />
                  <Bar dataKey="avgEngagement" fill="#1DA1F2" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Topic Details */}
          <div className="space-y-3">
            {clusters.map(cluster => (
              <div key={cluster.id} className="bg-surface-2 border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: cluster.color }}
                    />
                    <h4 className="font-bold text-text-primary">{cluster.name}</h4>
                  </div>
                  <span className="text-sm text-text-muted">
                    {cluster.tweets.length} {t('analytics.labels.tweet')}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {cluster.keywords.map(kw => (
                    <span
                      key={kw}
                      className="px-2 py-1 bg-surface-1 rounded text-xs text-text-primary"
                    >
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timing */}
      {selectedView === 'timing' && (
        <div className="space-y-6">
          {/* Heatmap (Simplified) */}
          <div className="bg-surface-2 border border-border rounded-xl p-6">
            <h3 className="text-lg font-bold text-text-primary mb-4">
              {t('analytics.timing.heatmap')}
            </h3>
            <p className="text-sm text-text-muted mb-4">
              {t('analytics.timing.heatmapDesc')}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="p-2 text-left text-text-muted">{t('analytics.timing.time')}</th>
                    {days.map((day: string) => (
                      <th key={day} className="p-2 text-center text-text-muted">{day}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 24 }, (_, hour) => (
                    <tr key={hour}>
                      <td className="p-2 text-text-muted">{hour}{t('analytics.timing.hour')}</td>
                      {[0, 1, 2, 3, 4, 5, 6].map(day => {
                        const slot = timeSlots.find(s => s.dayOfWeek === day && s.hour === hour);
                        const engagement = slot ? slot.avgEngagement : 0;
                        const maxEngagement = Math.max(...timeSlots.map(s => s.avgEngagement), 1);
                        const intensity = engagement / maxEngagement;
                        
                        return (
                          <td
                            key={day}
                            className="p-2 text-center"
                            style={{
                              backgroundColor: `rgba(29, 161, 242, ${intensity})`,
                              color: intensity > 0.5 ? '#ffffff' : '#71767B',
                            }}
                            title={`${engagement.toFixed(1)} ${t('analytics.timing.avgEngagement')}`}
                          >
                            {slot ? slot.count : ''}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Best Times */}
          <div className="bg-surface-2 border border-border rounded-xl p-6">
            <h3 className="text-lg font-bold text-text-primary mb-4">
              {t('analytics.timing.bestTitle')}
            </h3>
            <div className="space-y-3">
              {analyticsService.getBestTimeSlots(5).map((slot, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-surface-1 rounded-lg">
                    <div>
                      <p className="font-medium text-text-primary">
                        {idx + 1}. {days[slot.dayOfWeek]} {slot.hour}:00
                      </p>
                      <p className="text-xs text-text-muted">
                        {slot.count} {t('analytics.timing.postCount')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-brand">
                        {slot.avgEngagement.toFixed(1)}
                      </p>
                      <p className="text-xs text-text-muted">
                        {t('analytics.timing.avgEngagement')}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Trends */}
      {selectedView === 'trends' && (
        <div className="space-y-6">
          <div className="bg-surface-2 border border-border rounded-xl p-6">
            <h3 className="text-lg font-bold text-text-primary mb-4">
              {t('analytics.trends.title')}
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2E2E35" />
                <XAxis
                  dataKey="date"
                  stroke="#7B7B88"
                  tickFormatter={(date) => new Date(date).toLocaleDateString(locale === 'ja' ? 'ja-JP' : 'en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="#7B7B88" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#18181B', border: '1px solid #2E2E35' }}
                  labelStyle={{ color: '#F0F0F2' }}
                  labelFormatter={(date) => new Date(date).toLocaleDateString(locale === 'ja' ? 'ja-JP' : 'en-US')}
                />
                <Legend />
                <Line type="monotone" dataKey="tweets" stroke="#1DA1F2" name={t('analytics.trends.tweets')} />
                <Line type="monotone" dataKey="likes" stroke="#F59E0B" name={t('analytics.trends.likes')} />
                <Line type="monotone" dataKey="retweets" stroke="#00BA7C" name={t('analytics.trends.retweets')} />
                <Line type="monotone" dataKey="replies" stroke="#8B5CF6" name={t('analytics.trends.replies')} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};
